import React, { useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { BookOpen, CheckCircle2, Layers } from "lucide-react";
import type { PassagePage, ReadingQuestion } from "@examing/types";
import { RenderedHtml } from "modules/editor";
import { cn } from "lib/utils";
import { correctPassageValue } from "./correctValue";
import { resolvePassagePages, estimateReadingStats } from "./passagePages";
import {
  evaluateSubQuestion,
  asRecord,
  type EvaluatedSubQuestion,
} from "./passageEvaluation";
import { PassageReviewSubQuestionCard } from "./PassageReviewSubQuestionCard";

export interface PassageReviewProps {
  questionData?: Record<string, any> | null;
  answerRaw?: unknown;
  correctAnswerRaw?: unknown;
  /** Extra content under one sub-question, by its id (the AI analysis on the learner's own result). */
  renderSubQuestionFooter?: (subQuestionId: string) => React.ReactNode;
}

export const PassageReview: React.FC<PassageReviewProps> = ({
  questionData,
  answerRaw,
  correctAnswerRaw,
  renderSubQuestionFooter,
}) => {
  const { t } = useTranslation();

  const qd = questionData || {};
  const rawAnswers = useMemo(
    () => asRecord(answerRaw),
    [answerRaw],
  );

  // 1. Resolve structured pages from pages prop or fallback subQuestions
  const resolvedPages: PassagePage[] = useMemo(
    () =>
      resolvePassagePages({
        pages: qd.pages,
        subQuestions: qd.subQuestions,
        passageText: qd.passageText,
        passageTitle: qd.passageTitle,
        defaultTitle: (idx) => `${t("reading_passage")} ${idx + 1}`,
        generateId: (idx) => `page-${idx + 1}`,
      }),
    [qd.pages, qd.subQuestions, qd.passageText, qd.passageTitle, t],
  );

  // 2. Derive evaluated questions across all pages with global numbering
  const allEvaluatedQuestions = useMemo(() => {
    const rawSubQuestions: ReadingQuestion[] = Array.isArray(qd.subQuestions)
      ? qd.subQuestions
      : [];
    const keys =
      correctAnswerRaw && typeof correctAnswerRaw === "object"
        ? (correctAnswerRaw as Record<string, unknown>)
        : correctPassageValue(rawSubQuestions);

    let globalCount = 0;
    const pageEvaluations: EvaluatedSubQuestion[][] = [];

    for (const page of resolvedPages) {
      const pageList: EvaluatedSubQuestion[] = [];
      for (const sub of page.subQuestions || []) {
        const evaluated = evaluateSubQuestion(
          sub,
          globalCount + 1,
          rawAnswers[sub.id],
          keys[sub.id],
        );
        pageList.push(evaluated);
        globalCount++;
      }
      pageEvaluations.push(pageList);
    }

    return {
      flat: pageEvaluations.flat(),
      byPage: pageEvaluations,
      totalCount: globalCount,
    };
  }, [resolvedPages, qd.subQuestions, rawAnswers, correctAnswerRaw]);

  const [activePageIndex, setActivePageIndex] = useState(0);
  const [textSize, setTextSize] = useState<"sm" | "md" | "lg">("md");
  const [filterMode, setFilterMode] = useState<"all" | "incorrect" | "correct">("all");
  const [mobileTab, setMobileTab] = useState<"passage" | "questions">("passage");
  const [highlightedQId, setHighlightedQId] = useState<string | null>(null);

  const questionRefs = useRef<Record<string, HTMLDivElement | null>>({});

  const safePageIndex = Math.min(
    Math.max(0, activePageIndex),
    Math.max(0, resolvedPages.length - 1),
  );
  const activePage = resolvedPages[safePageIndex] || resolvedPages[0];
  const activeEvaluations = useMemo(
    () => allEvaluatedQuestions.byPage[safePageIndex] || [],
    [allEvaluatedQuestions.byPage, safePageIndex],
  );

  // Filtered subquestions on active page
  const displayedEvaluations = useMemo(() => {
    if (filterMode === "all") return activeEvaluations;
    if (filterMode === "incorrect")
      return activeEvaluations.filter((eq) => !eq.isCorrect);
    return activeEvaluations.filter((eq) => eq.isCorrect);
  }, [activeEvaluations, filterMode]);

  const pageCorrect = activeEvaluations.filter((q) => q.isCorrect).length;

  // Overall totals
  const totalCorrect = allEvaluatedQuestions.flat.filter((q) => q.isCorrect).length;
  const totalQuestions = allEvaluatedQuestions.totalCount;

  // Jump to specific question
  const jumpToQuestion = (targetEvaluated: EvaluatedSubQuestion) => {
    // Find which page contains this question
    const targetPageIndex = allEvaluatedQuestions.byPage.findIndex((pageList) =>
      pageList.some((eq) => eq.question.id === targetEvaluated.question.id),
    );

    if (targetPageIndex !== -1 && targetPageIndex !== activePageIndex) {
      setActivePageIndex(targetPageIndex);
    }
    setMobileTab("questions");
    setHighlightedQId(targetEvaluated.question.id);

    setTimeout(() => {
      const el = questionRefs.current[targetEvaluated.question.id];
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    }, 80);

    setTimeout(() => {
      setHighlightedQId(null);
    }, 2000);
  };

  // Reading metrics
  const { wordCount, readTimeMin: readingTimeMin } = useMemo(
    () => estimateReadingStats(activePage?.passageText || ""),
    [activePage?.passageText],
  );

  return (
    <div className="space-y-4">
      {/* Top Passage Pages Selector (Tabs) */}
      {resolvedPages.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 border-b border-border/70 pb-3">
          <div className="mr-1 flex items-center gap-1.5 text-[13px] font-semibold text-muted-foreground">
            <Layers className="h-4 w-4" aria-hidden />
            {t("passages")}:
          </div>
          <div className="flex flex-wrap gap-1.5">
            {resolvedPages.map((p, idx) => {
              const pageQuestions = allEvaluatedQuestions.byPage[idx] || [];
              const correctInPage = pageQuestions.filter((q) => q.isCorrect).length;
              const totalInPage = pageQuestions.length;
              const startQ = pageQuestions[0]?.globalIndex || 1;
              const endQ =
                pageQuestions[pageQuestions.length - 1]?.globalIndex || startQ;
              const isAllCorrect = totalInPage > 0 && correctInPage === totalInPage;
              const hasErrors = totalInPage > 0 && correctInPage < totalInPage;
              const isActive = idx === safePageIndex;

              return (
                <button
                  key={p.id || idx}
                  type="button"
                  onClick={() => {
                    setActivePageIndex(idx);
                    setFilterMode("all");
                  }}
                  title={p.passageTitle || `${t("passage")} ${idx + 1}`}
                  className={cn(
                    "flex items-center gap-1.5 sm:gap-2 px-2.5 sm:px-3 py-1 sm:py-1.5 rounded-lg text-xs font-medium transition-all border max-w-full min-w-0",
                    isActive
                      ? "border-foreground bg-foreground text-background"
                      : "bg-card hover:bg-muted/70 text-foreground border-border/80",
                  )}
                >
                  <span className="font-semibold truncate max-w-[90px] sm:max-w-[160px]">
                    {p.passageTitle || `${t("passage")} ${idx + 1}`}
                  </span>
                  <span
                    className={cn(
                      "text-[11px] px-1.5 py-0.5 rounded-full font-semibold tabular-nums shrink-0",
                      isActive
                        ? "bg-background/20 text-background"
                        : isAllCorrect
                          ? "bg-success-50 text-success-700 dark:bg-success-900/30 dark:text-success-300"
                          : hasErrors
                            ? "bg-error-50 text-error-700 dark:bg-error-900/30 dark:text-error-300"
                            : "bg-muted text-muted-foreground",
                    )}
                  >
                    <span className="hidden sm:inline">Q{startQ}–{endQ} · </span>{correctInPage}/{totalInPage}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="ml-auto text-xs text-muted-foreground font-medium">
            {t("score")}:{" "}
            <span className="font-bold text-foreground">
              {totalCorrect}/{totalQuestions}
            </span>{" "}
            ({totalQuestions ? Math.round((totalCorrect / totalQuestions) * 100) : 0}%)
          </div>
        </div>
      )}

      {/* Mobile Tab Switcher */}
      <div className="flex lg:hidden rounded-lg bg-muted p-1 gap-1">
        <button
          type="button"
          onClick={() => setMobileTab("passage")}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-1.5 text-xs font-semibold rounded-md transition-colors",
            mobileTab === "passage"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <BookOpen className="h-3.5 w-3.5" />
          {t("reading_passage")}
        </button>
        <button
          type="button"
          onClick={() => setMobileTab("questions")}
          className={cn(
            "flex-1 flex items-center justify-center gap-2 py-1.5 text-xs font-semibold rounded-md transition-colors",
            mobileTab === "questions"
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <CheckCircle2 className="h-3.5 w-3.5" />
          {t("questions")} ({activeEvaluations.filter((q) => q.isCorrect).length}/
          {activeEvaluations.length})
        </button>
      </div>

      {/* Two-Pane IELTS Reading Environment */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
        {/* LEFT PANE: Reading Passage Material */}
        <div
          className={cn(
            "flex flex-col rounded-xl border border-border/80 bg-card overflow-hidden shadow-xs",
            mobileTab !== "passage" && "hidden lg:flex",
          )}
        >
          {/* Passage Toolbar */}
          <div className="flex items-center justify-between gap-3 px-3 sm:px-4 py-2 sm:py-3 bg-muted/40 border-b border-border/70 min-w-0">
            <div className="flex items-center gap-2 min-w-0 flex-1">
              <BookOpen className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
              <h4
                className="text-xs sm:text-sm font-bold text-foreground truncate min-w-0 flex-1"
                title={activePage.passageTitle || undefined}
              >
                {activePage.passageTitle ||
                  `${t("reading_passage")} ${safePageIndex + 1}`}
              </h4>
            </div>

            <div className="flex items-center gap-3 shrink-0">
              {wordCount > 0 && (
                <span className="hidden sm:inline-block text-[11px] text-muted-foreground font-medium">
                  {wordCount} {t("words")} · ~{readingTimeMin} {t("min_read")}
                </span>
              )}

              <Segmented<typeof textSize>
                value={textSize}
                onChange={setTextSize}
                options={[
                  { value: "sm", label: "A-", title: "Smaller text" },
                  { value: "md", label: "A", title: "Standard text" },
                  { value: "lg", label: "A+", title: "Larger text" },
                ]}
              />
            </div>
          </div>

          {/* Passage Content Container */}
          <div
            className={cn(
              "p-5 overflow-y-auto max-h-[640px] leading-relaxed scrollbar-thin text-foreground",
              textSize === "sm" && "text-sm",
              textSize === "md" && "text-base",
              textSize === "lg" && "text-lg",
            )}
          >
            {activePage.passageText ? (
              <RenderedHtml html={activePage.passageText} />
            ) : (
              <p className="text-muted-foreground italic text-sm">
                — {t("no_passage_text_provided")} —
              </p>
            )}
          </div>
        </div>

        {/* RIGHT PANE: Subquestions Answer Review */}
        <div
          className={cn(
            "flex flex-col rounded-xl border border-border/80 bg-card overflow-hidden shadow-xs",
            mobileTab !== "questions" && "hidden lg:flex",
          )}
        >
          <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 bg-muted/40 border-b border-border/70">
            <p className="flex items-baseline gap-2">
              <span className="text-sm font-semibold text-foreground">
                {t("questions")}
              </span>
              <span className="text-xs tabular-nums text-muted-foreground">
                {pageCorrect}/{activeEvaluations.length} {t("correct")}
              </span>
            </p>
            <Segmented<typeof filterMode>
              value={filterMode}
              onChange={setFilterMode}
              options={[
                { value: "all", label: <FilterLabel text={t("all")} count={activeEvaluations.length} /> },
                { value: "incorrect", label: <FilterLabel text={t("incorrect")} count={activeEvaluations.length - pageCorrect} /> },
                { value: "correct", label: <FilterLabel text={t("correct")} count={pageCorrect} /> },
              ]}
            />
          </div>

          {/* Subquestions List Container */}
          <div className="divide-y divide-border/70 overflow-y-auto max-h-[640px] scrollbar-thin">
            {displayedEvaluations.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">
                {filterMode === "incorrect"
                  ? t("all_answers_correct_in_page")
                  : t("no_questions_match_filter")}
              </div>
            ) : (
              displayedEvaluations.map((eq) => (
                <PassageReviewSubQuestionCard
                  key={eq.question.id}
                  evaluated={eq}
                  isHighlighted={highlightedQId === eq.question.id}
                  footer={renderSubQuestionFooter?.(eq.question.id)}
                  onCardRef={(el) => {
                    questionRefs.current[eq.question.id] = el;
                  }}
                />
              ))
            )}
          </div>
        </div>
      </div>

      {/* Question jump strip */}
      <div className="rounded-xl border border-border/80 bg-card p-4">
        <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-2">
          <p className="text-sm font-semibold text-foreground">
            {t("question_navigator")}
            <span className="ml-2 font-normal tabular-nums text-muted-foreground">
              {totalCorrect}/{totalQuestions} {t("correct")}
            </span>
          </p>
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <LegendDot className="bg-green-500" label={t("correct")} />
            <LegendDot className="bg-red-500" label={t("incorrect")} />
            <LegendDot className="bg-muted-foreground/40" label={t("not_answered")} />
          </div>
        </div>

        <div className="mt-3 flex flex-wrap gap-1.5">
          {allEvaluatedQuestions.flat.map((eq) => {
            // With several passages, questions outside the open one step back.
            const onOtherPage =
              resolvedPages.length > 1 &&
              !activeEvaluations.some((ae) => ae.question.id === eq.question.id);

            return (
              <button
                key={eq.question.id}
                type="button"
                onClick={() => jumpToQuestion(eq)}
                title={`${t("question")} ${eq.globalIndex}: ${eq.isCorrect ? t("correct") : eq.isAnswered ? t("incorrect") : t("not_answered")}`}
                className={cn(
                  "flex h-8 min-w-[2rem] items-center justify-center rounded-md border px-2 text-xs font-semibold tabular-nums transition-[background-color,opacity] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  eq.isCorrect
                    ? "border-green-200 bg-green-50 text-green-800 hover:bg-green-100 dark:border-green-900/60 dark:bg-green-950/40 dark:text-green-300"
                    : eq.isAnswered
                      ? "border-red-200 bg-red-50 text-red-700 hover:bg-red-100 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-300"
                      : "border-border bg-card text-muted-foreground hover:bg-muted",
                  onOtherPage && "opacity-50 hover:opacity-100",
                )}
              >
                {eq.globalIndex}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

interface SegmentedProps<T extends string> {
  value: T;
  onChange: (value: T) => void;
  options: Array<{ value: T; label: React.ReactNode; title?: string }>;
}

function Segmented<T extends string>({ value, onChange, options }: SegmentedProps<T>) {
  return (
    <div className="inline-flex items-center gap-0.5 rounded-lg bg-muted p-0.5 text-xs">
      {options.map((option) => (
        <button
          key={option.value}
          type="button"
          title={option.title}
          aria-pressed={value === option.value}
          onClick={() => onChange(option.value)}
          className={cn(
            "rounded-md px-2 py-1 font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            value === option.value
              ? "bg-background text-foreground shadow-sm"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          {option.label}
        </button>
      ))}
    </div>
  );
}

const FilterLabel = ({ text, count }: { text: string; count: number }) => (
  <>
    {text} <span className="tabular-nums text-muted-foreground">{count}</span>
  </>
);

const LegendDot = ({ className, label }: { className: string; label: string }) => (
  <span className="flex items-center gap-1.5">
    <span className={cn("h-2 w-2 rounded-full", className)} />
    {label}
  </span>
);

export default PassageReview;
